import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import {
  DocumentTemplate,
  EntityMeta,
  ITemplateData,
  TemplateDataHistory,
} from '@qtek/shared/models';
import { DocumentTemplatesActions } from './document-templates.actions';

export interface DocumentTemplatesState {
  documentTemplates: DocumentTemplate[];
  templateData: ITemplateData | null;
  lastPublishedTemplateData: ITemplateData | null;
  templateDataHistory: TemplateDataHistory | null;
  templatesMeta: EntityMeta | undefined;
  templatesError: Error | null;
  templatesIsLoading: boolean;
  isTemplateDataLoading: boolean;
}

export const documentTemplatesInitialState: DocumentTemplatesState = {
  documentTemplates: [],
  templateData: null,
  lastPublishedTemplateData: null,
  templateDataHistory: null,
  templatesMeta: undefined,
  templatesError: null,
  templatesIsLoading: false,
  isTemplateDataLoading: false,
};
export const DocumentTemplatesReducer = createReducer(
  documentTemplatesInitialState,
  on(DocumentTemplatesActions.connectDocumentTemplates, state => {
    return {
      ...state,
      templatesIsLoading: true,
    };
  }),
  on(
    DocumentTemplatesActions.queryDocumentTemplatesSuccess,
    (state, { documentTemplates, meta }) => {
      return {
        ...state,
        documentTemplates,
        templatesMeta: meta,
        templatesIsLoading: false,
      };
    }
  ),
  on(DocumentTemplatesActions.queryDocumentTemplatesFailure, state => {
    return {
      ...state,
      templatesIsLoading: false,
    };
  }),
  on(
    DocumentTemplatesActions.createDocumentTemplatesSuccess,
    (state, { documentTemplate }) => {
      return {
        ...state,
        documentTemplates: [
          ...(state.documentTemplates || []),
          documentTemplate,
        ],
        templatesIsLoading: false,
      };
    }
  ),
  on(DocumentTemplatesActions.createDocumentTemplatesFailure, state => {
    return {
      ...state,
      templatesIsLoading: false,
    };
  }),
  on(
    DocumentTemplatesActions.updateDocumentTemplatesSuccess,
    (state, { payload }) => {
      return {
        ...state,
        documentTemplates: state.documentTemplates.map(template =>
          template._id === payload._id ? payload : template
        ),
        templatesIsLoading: false,
      };
    }
  ),
  on(DocumentTemplatesActions.updateDocumentTemplatesFailure, state => {
    return {
      ...state,
      templatesIsLoading: false,
    };
  }),
  on(
    DocumentTemplatesActions.deleteDocumentTemplatesSuccess,
    (state, { documentTemplateId }) => {
      return {
        ...state,
        documentTemplates: state.documentTemplates.filter(
          documentTemplate => documentTemplate._id !== documentTemplateId
        ),
        templatesIsLoading: false,
      };
    }
  ),
  on(DocumentTemplatesActions.deleteDocumentTemplatesFailure, state => {
    return {
      ...state,
      templatesIsLoading: false,
    };
  }),
  on(DocumentTemplatesActions.getTemplateData, state => {
    return {
      ...state,
      isTemplateDataLoading: true,
    };
  }),
  on(
    DocumentTemplatesActions.getTemplateDataSuccess,
    (state, { templateData }) => {
      return {
        ...state,
        templateData,
        isTemplateDataLoading: false,
      };
    }
  ),
  on(DocumentTemplatesActions.getTemplateDataFailure, (state, { error }) => {
    return {
      ...state,
      templatesError: error,
      isTemplateDataLoading: false,
    };
  }),
  on(DocumentTemplatesActions.disconnectTemplateData, state => {
    return {
      ...state,
      templateData: null as ITemplateData | null,
      templateDataHistory: null as ITemplateData[] | null,
      lastPublishedTemplateData: null as ITemplateData | null,
      isTemplateDataLoading: false,
    };
  }),

  on(
    DocumentTemplatesActions.getTemplateDataHistorySuccess,
    (state, { templateDataHistory }) => {
      return {
        ...state,
        templateDataHistory,
      };
    }
  ),
  on(
    DocumentTemplatesActions.getTemplateDataHistoryFailure,
    (state, { error }) => {
      return {
        ...state,
        templatesError: error,
      };
    }
  ),
  on(
    DocumentTemplatesActions.getLastPublishedTemplateDataSuccess,
    (state, { templateData }) => {
      return {
        ...state,
        lastPublishedTemplateData: templateData,
      };
    }
  ),
  on(
    DocumentTemplatesActions.getLastPublishedTemplateDataFailure,
    (state, { error }) => {
      return {
        ...state,
        templatesError: error,
        lastPublishedTemplateData: null as ITemplateData,
      };
    }
  )
);

export const DocumentTemplatesFeature = createFeature({
  name: 'DocumentTemplatesKey',
  reducer: DocumentTemplatesReducer,
  extraSelectors: ({ selectDocumentTemplates }) => ({
    selectTemplateById: (id: string) =>
      createSelector(selectDocumentTemplates, documentTemplates =>
        documentTemplates.find(template => template._id === id)
      ),
    selectTemplatesByType: (type: DocumentTemplate['tp']) =>
      createSelector(selectDocumentTemplates, documentTemplates =>
        documentTemplates.filter(template => template.tp === type)
      ),
  }),
});

export const {
  selectDocumentTemplates,
  selectTemplatesMeta,
  selectTemplatesError,
  selectTemplatesIsLoading,
  selectIsTemplateDataLoading,
  selectTemplateById,
  selectTemplatesByType,
  selectTemplateData,
  selectLastPublishedTemplateData,
  selectTemplateDataHistory,
} = DocumentTemplatesFeature;
